import React from 'react';
import Intercom from '@svg/intercom';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { useDispatch } from 'react-redux';
import { flowActions } from '@store/index';
import { Label } from '@common/Label';
import { Stack, alpha } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../../../styles';
import { Secrets } from '@common/Autocomplete/Secrets';
import { useSecrets } from '@common/hooks/useSecrets';
import { IntegrationSecret } from '@requests/secrets';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  open: string | null;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    setSecretIdentifier: React.Dispatch<React.SetStateAction<string>>;
  };
}

export const IntercomSecret: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { secretIdentifier: secretName, setSecretIdentifier: setSecretName } = configuration;
  const { service_id } = useParams();
  const { secrets, loading, refetch } = useSecrets();

  const tooltip = 'Select the secret in your workspace that holds your Intercom API key.';
  const isNoSecrets = secrets.length === 0;

  if (isNoSecrets) {
    return (
      <Modal
        open={open === 'intercom' && page === 0}
        setOpen={reset}
        background={alpha(theme.text.standard, 0.06)}
        header='No Secrets Found'
        info='There are no secrets configured in your workspace. Please create a secret with your Intercom API key to continue.'
        icon={<Intercom color={theme.text.standard} height={21} />}
        actions={
          <Stack direction='row' spacing={1}>
            <Button onClick={reset}>Back</Button>
            <Button
              customColor='primary'
              onClick={() => {
                reset();
                navigate(`/${service_id}/secrets`);
                dispatch(flowActions.setCreateSecret(true));
              }}
            >
              Create a Secret
            </Button>
          </Stack>
        }
      />
    );
  }

  const body = (
    <Container>
      <Label title='Intercom API Key' tooltip={tooltip} />
      <Secrets
        refetch={refetch}
        value={secretName}
        loading={loading}
        secrets={secrets.filter((secret) => secret.type === 'bearer')}
        onChange={(_, item: { value: IntegrationSecret }) => {
          if (!item?.value) return;
          setSecretName(item.value.identifier);
        }}
        placeholder='INTERCOM_API_KEY'
      />
    </Container>
  );

  const actions = (
    <Stack spacing={1} direction='row'>
      <Button onClick={reset}>Cancel</Button>
      <Button disabled={!secretName} customColor='primary' onClick={() => setPage(1)}>
        Set API Key
      </Button>
    </Stack>
  );

  return (
    <Modal
      open={open === 'intercom' && page === 0}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Intercom'
      info={tooltip}
      icon={<Intercom color={theme.text.standard} height={21} />}
      body={body}
      actions={actions}
    />
  );
};
